import { Component, type ErrorInfo, type ReactNode } from "react";
import { captureException } from "../autter";

type AutterErrorBoundaryProps = {
  /** Rendered when a child throws during render. Defaults to null. */
  fallback?: ReactNode;
  /** Called after the error is reported to Autter. */
  onError?: (error: Error, info: ErrorInfo) => void;
  children?: ReactNode;
};

type AutterErrorBoundaryState = {
  hasError: boolean;
};

// Copied from @autter/runtime-next's boundary (~30 lines) so this Vite React
// SPA gets React render-error capture without pulling Next.js in. React render
// errors do NOT hit `window.onerror`, so the browser tracker alone misses them.
export class AutterErrorBoundary extends Component<
  AutterErrorBoundaryProps,
  AutterErrorBoundaryState
> {
  state: AutterErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): AutterErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    captureException(error, {
      react: true,
      ...(info.componentStack
        ? { componentStack: String(info.componentStack).slice(0, 4_000) }
        : {}),
    });
    this.props.onError?.(error, info);
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback ?? null;
    }
    return this.props.children;
  }
}
